import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, TextField, Typography} from "@mui/material";
import SuperDocTemplateBuilder from "@superdoc-dev/template-builder";
import "superdoc/style.css";
import {useEffect, useRef, useState} from "react";
import {toast} from "react-hot-toast";
import {axios_tenant} from "../../../../axios";
import "./DocxEditor.css";

// Converts a `data:<mime>;base64,<data>` string into a Blob, the same
// atob/Uint8Array approach docgen-apis's own PdfFormPreview uses - kept
// local instead of `fetch(dataUri)` so decoding never depends on whatever
// CSP connect-src happens to allow.
const base64DataUriToBlob = (dataUri) => {
    const [header, base64] = dataUri.split(",");
    const mime = header.match(/data:(.*);base64/)?.[1] || "application/octet-stream";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
};

// Module 2 — Generate Existing Custom Form. Left panel lists/searches saved
// Custom Forms (GET /customForms), right panel previews the selected one
// read-only. GET /customForms/:id/preview returns the docx as base64 (not a
// presigned S3 URL) so the browser only ever talks to this API - never
// directly to S3 - keeping it inside the frontend's CSP connect-src without
// needing S3 whitelisted there. The decoded Blob is fed into SuperDoc's own
// "viewing" mode so we don't need a separate docx renderer.
// Several forms can be ticked at once; "Select Forms" hands their ids back to
// Lex as the `form_id` slot, comma-separated (customforms_handler.py's
// _parse_form_ids splits them) - the application/publication/patent numbers
// step that follows is NOT handled
// here; once `form_id` is filled, Lex elicits the standard `application_nums`
// slot and the existing generic chat-box path (same one Bibliographic Data
// Extractor uses) takes over.
const GenerateCustomFormModal = ({ open, onClose, onSelected, handleModuleSubmit, isAdmin = false }) => {
    const [search, setSearch] = useState("");
    const [forms, setForms] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    // Ticked forms, in the order they were picked, and the one shown in the
    // preview panel (the last row clicked) - independent, so unticking a form
    // doesn't blank the preview.
    const [selectedForms, setSelectedForms] = useState([]);
    const [previewForm, setPreviewForm] = useState(null);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [hasComments, setHasComments] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const previewSeqRef = useRef(0);
    const previewTemplateRef = useRef(null);
    const listSeqRef = useRef(0);

    const handlePreviewReady = () => {
        const superdoc = previewTemplateRef.current?.getSuperDoc?.();
        setHasComments((superdoc?.commentsStore?.commentsList?.length || 0) > 0);
    };

    const fetchForms = () => {
        const seq = ++listSeqRef.current;
        setListLoading(true);
        return axios_tenant
            .get("/trexo-assistant/customForms", { params: search ? { search } : {} })
            .then((res) => {
                if (listSeqRef.current === seq) setForms(res?.data?.forms || []);
            })
            .catch(() => {
                if (listSeqRef.current === seq) setForms([]);
            })
            .finally(() => {
                if (listSeqRef.current === seq) setListLoading(false);
            });
    };

    useEffect(() => {
        if (!open) return;

        const handle = setTimeout(fetchForms, 300);
        return () => clearTimeout(handle);
    }, [open, search]);

    const resetLocalState = () => {
        setSearch("");
        setForms([]);
        setSelectedForms([]);
        setPreviewForm(null);
        setPreviewBlob(null);
        setHasComments(false);
    };

    const handleClose = () => {
        resetLocalState();
        onClose();
    };

    const isSelected = (form) => selectedForms.some((selected) => selected.id === form.id);

    const toggleFormSelection = (form) => {
        setSelectedForms((prev) =>
            prev.some((selected) => selected.id === form.id)
                ? prev.filter((selected) => selected.id !== form.id)
                : [...prev, form]
        );
    };

    // Clicking a row previews it and ticks it; the checkbox alone toggles.
    const handleSelectForm = (form) => {
        if (!isSelected(form)) {
            setSelectedForms((prev) => [...prev, form]);
        }
        if (previewForm?.id === form.id) return;
        setPreviewForm(form);
        setPreviewBlob(null);
        setPreviewLoading(true);
        setHasComments(false);

        const seq = ++previewSeqRef.current;
        axios_tenant
            .get(`/trexo-assistant/customForms/${form.id}/preview`)
            .then((res) => {
                const base64 = res?.data?.base64;
                if (previewSeqRef.current === seq) setPreviewBlob(base64 ? base64DataUriToBlob(base64) : null);
            })
            .catch(() => {
                if (previewSeqRef.current === seq) setPreviewBlob(null);
            })
            .finally(() => {
                if (previewSeqRef.current === seq) setPreviewLoading(false);
            });
    };

    const handleDeleteForm = (form, e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete "${form.formName}"? This cannot be undone.`)) return;

        setDeletingId(form.id);
        axios_tenant
            .delete(`/trexo-assistant/customForms/${form.id}`)
            .then(() => {
                toast.success(`"${form.formName}" deleted.`);
                setSelectedForms((prev) => prev.filter((selected) => selected.id !== form.id));
                if (previewForm?.id === form.id) {
                    setPreviewForm(null);
                    setPreviewBlob(null);
                    setHasComments(false);
                }
                fetchForms();
            })
            .catch(() => toast.error(`Failed to delete "${form.formName}".`))
            .finally(() => setDeletingId(null));
    };

    const handleConfirm = () => {
        if (selectedForms.length === 0) return;
        // This modal only opens while Lex is eliciting GenerateCustomFormIntent's
        // `form_id` slot (see TrexoChat.js's slotNameCheck === "form_id" branch), so
        // the submitted text becomes that slot's interpretedValue - it must be the
        // forms' numeric ids, not their display names, or customforms_handler.py's
        // handle_generate_custom_form_intent has no id to look up. The chat bubble
        // only shows the form names so the internal ids aren't exposed in the UI.
        handleModuleSubmit({
            textInput: selectedForms.map((form) => form.id).join(","),
            displayText: selectedForms.map((form) => form.formName).join(", ")
        });
        resetLocalState();
        onSelected?.();
    };

    if (!open) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 1300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(15, 23, 42, 0.5)"
            }}
        >
            <Box
                sx={{

                    width: hasComments ? "95%" : "90%",
                    maxWidth: hasComments ? "1600px" : "1200px",
                    height: "85vh",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: 24
                }}
            >
                <div className={"docx-editor-container" + (hasComments ? " has-comments" : "")} style={{ height: "100%" }}>
                    <div className="docx-editor-header">
                        <div>
                            <div className="docx-editor-title">Existing Forms</div>
                            <p className="docx-fields-sidebar-hint" style={{ margin: "4px 0 0 0" }}>
                                Select one or more forms to generate
                            </p>
                        </div>
                        <div className="docx-editor-actions">
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirm}
                                disabled={selectedForms.length === 0}
                                sx={{ fontSize: "13px", fontWeight: 600, px: 3, textTransform: "none" }}
                            >
                                {selectedForms.length > 1 ? `Select Forms (${selectedForms.length})` : "Select Form"}
                            </Button>
                            <IconButton size="small" onClick={handleClose} sx={{ color: "#94A3B8" }}>
                                <CloseIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </div>
                    </div>

                    <div className="docx-editor-body">
                        <div className="docx-editor-workspace">
                            <aside className="docx-fields-sidebar" style={{ width: "260px" }}>
                                <div className="docx-fields-sidebar-title">Forms</div>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchOutlinedIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ mb: 1.5, mt: 0.5 }}
                                />

                                {listLoading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                        <CircularProgress size={22} />
                                    </Box>
                                ) : forms.length === 0 ? (
                                    <Typography sx={{ fontSize: "12px", color: "#94A3B8", textAlign: "center", py: 3 }}>
                                        No Custom Forms found.
                                    </Typography>
                                ) : (
                                    <ul className="docx-fields-list">
                                        {forms.map((form) => (
                                            <li
                                                key={form.id}
                                                className="docx-field-item"
                                                style={{
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    ...(previewForm?.id === form.id
                                                        ? { background: "#EAF2FF", borderColor: "#2563EB" }
                                                        : {})
                                                }}
                                                onClick={() => handleSelectForm(form)}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={isSelected(form)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleFormSelection(form)}
                                                    inputProps={{ "aria-label": `Select ${form.formName}` }}
                                                    sx={{ p: 0.25 }}
                                                />
                                                <span className="docx-field-item-label" style={{ flex: 1, minWidth: 0 }}>
                                                    {form.formName}
                                                    {form.databaseLabel && (
                                                        <span style={{ display: "block", fontSize: "10px", color: "#94A3B8", fontWeight: 400 }}>
                                                            {form.databaseLabel}
                                                        </span>
                                                    )}
                                                </span>
                                                {isAdmin && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Delete ${form.formName}`}
                                                        disabled={deletingId === form.id}
                                                        onClick={(e) => handleDeleteForm(form, e)}
                                                    >
                                                        {deletingId === form.id ? (
                                                            <CircularProgress size={14} />
                                                        ) : (
                                                            <DeleteOutlineIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                                                        )}
                                                    </IconButton>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </aside>

                            <div className="docx-editor-document-area">
                                {!previewForm ? (
                                    <div className="docx-fields-sidebar-hint" style={{ padding: "24px", textAlign: "center" }}>
                                        Select a form on the left to preview it.
                                    </div>
                                ) : previewLoading ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : previewBlob ? (
                                    <SuperDocTemplateBuilder
                                        key={previewForm.id}
                                        ref={previewTemplateRef}
                                        document={{ source: previewBlob, mode: "viewing" }}
                                        toolbar={false}
                                        documentHeight="100%"
                                        onReady={handlePreviewReady}
                                    />
                                ) : (
                                    <div className="docx-fields-sidebar-hint" style={{ padding: "24px", textAlign: "center" }}>
                                        Couldn't load a preview for this form.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Box>
        </Box>
    );
};

export default GenerateCustomFormModal;